import cx from 'classnames';
import {find, forEach, isNumber, map, isEqual, some} from 'lodash';
import moment from 'moment';
import {useState, useCallback, Fragment, useEffect, useContext} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, Timer, RequiredMark} from 'components';
import {UserSessionContext} from 'context';
import {usePrevious} from 'hooks';
import {AnyParticipant, AsyncRequestStatusName, AsyncRequestTypeName, ClientParticipant} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {sessionUtils} from 'utils';
import {buildTranslationKey} from 'utils/transform';

import {StatusIcon} from '../StatusIcon';
import {InSessionAction} from '../types';

const ASYNC_REQUEST_T_KEYS = TRANSLATION_KEYS.COMMON.SESSION.ASYNC_REQUEST.TYPE;

type Props = {
    participant: AnyParticipant;
    className?: string;
    readonly?: boolean;
};

type AsyncRequestState = {
    resetAfterDate: Date;
    resetTimeLeft: boolean;
}

export const ParticipantAsyncRequests: React.FC<Props> = ({className, participant, readonly}: Props) => {
    const {t} = useTranslation();
    const {createParticipantAsyncRequest, resetParticipantAsyncRequest, showParticipantAsyncRequestDetails, inProgressActions} =
        useContext(UserSessionContext);
    const [states, setStates] = useState<{[key in AsyncRequestTypeName]: AsyncRequestState}>({} as any);
    const prevParticipant = usePrevious(participant);

    const getAsyncRequest = useCallback(
        (type: AsyncRequestTypeName, p: AnyParticipant = participant) => find(p.asyncRequests, {type}),
        [participant]);

    const resetEnabled = useCallback((type: AsyncRequestTypeName) => {
        const asyncRequest = getAsyncRequest(type);
        const {mayResetAfterSeconds} = asyncRequest;
        return isNumber(mayResetAfterSeconds) && mayResetAfterSeconds > -1;
    }, [getAsyncRequest]);

    const getAsyncRequestState = useCallback((type: AsyncRequestTypeName) => {
        if (!resetEnabled(type)) {
            return;
        }
        const {resetTimeLeft} = states[type] || {};
        const prevStatus = prevParticipant ? getAsyncRequest(type, prevParticipant)?.status?.name : null;
        const status = getAsyncRequest(type)?.status?.name;
        const statusChanged = prevStatus !== status;

        if ((!status || [AsyncRequestStatusName.Success, AsyncRequestStatusName.Failed].includes(status)) && resetTimeLeft) {
            return {resetAfterDate: null, resetTimeLeft: false};
        } else if ([AsyncRequestStatusName.Requested, AsyncRequestStatusName.InProgress].includes(status) &&
            statusChanged && !resetTimeLeft) {
            const asyncRequest = getAsyncRequest(type);
            const {mayResetAfterSeconds} = asyncRequest;
            const requestDate = asyncRequest.status.date;
            const mayResetRightAfter = mayResetAfterSeconds === 0;
            const resetAfterDate = mayResetRightAfter ? null : moment(requestDate).add(mayResetAfterSeconds, 's').toDate();
            return {
                resetAfterDate,
                resetTimeLeft: !mayResetRightAfter && resetAfterDate > new Date()
            };
        } else {
            return states?.[type];
        }
    }, [prevParticipant, states, getAsyncRequest, resetEnabled]);

    const syncState = useCallback((type: AsyncRequestTypeName) => {
        setStates((s) => ({...s, [type]: getAsyncRequestState(type)}));
    }, [getAsyncRequestState]);

    const onTimerChange = useCallback((type: AsyncRequestTypeName) => {
        const {resetAfterDate} = states[type] || {};
        const resetTimeLeft = resetAfterDate > new Date();
        const state = {resetTimeLeft, resetAfterDate, ...(!resetTimeLeft && {resetAfterDate: null})};
        setStates((s) => ({...s, [type]: state}));
    }, [states]);

    const canRequestByData = useCallback((type: AsyncRequestTypeName) => {
        const clientParticipant = participant as ClientParticipant;
        if (type === AsyncRequestTypeName.SumaEmailVerification) {
            return !!clientParticipant.data?.userInfo?.email;
        } else if (type === AsyncRequestTypeName.SumaPhoneVerification) {
            return !!clientParticipant.data?.userInfo?.phone;
        }
        return true;
    }, [participant]);

    useEffect(() => {
        const statuses = map(participant?.asyncRequests, 'status.name');
        const oldStatuses = map(prevParticipant?.asyncRequests, 'status.name');
        if (!isEqual(statuses, oldStatuses)) {
            forEach(participant.asyncRequests, ({type}) => syncState(type));
        }
    }, [prevParticipant, participant, syncState]);

    return (
        <div className={className}>
            {map(participant.asyncRequests, ({type, status, required}) => {
                const statusName = status?.name;
                const {resetTimeLeft, resetAfterDate} = states[type] || {};
                const isSuccess = statusName === AsyncRequestStatusName.Success;
                const isFailed = statusName === AsyncRequestStatusName.Failed;
                const canRequestByStatus = sessionUtils.canMakeAsyncRequest(participant, type);
                const apiRequestInProgress = some(inProgressActions, ({action, data}) =>
                    action === InSessionAction.ToggleAsyncRequest &&
                    data.asyncRequestType === type &&
                    data.participantId === participant.id
                );
                const statusInProgress = [AsyncRequestStatusName.Requested, AsyncRequestStatusName.InProgress].includes(statusName);
                const inProgress = apiRequestInProgress || statusInProgress;
                const canRequest = canRequestByStatus && !apiRequestInProgress && !readonly && canRequestByData(type);
                const canReset = !apiRequestInProgress && statusInProgress && resetEnabled(type) && !resetTimeLeft && !readonly;
                const clickable = !apiRequestInProgress && (isSuccess || isFailed);

                return (
                    <Fragment key={type}>
                        <div className='tw-flex tw-justify-between tw-mt-2'>
                            <div className='tw-flex-1'>
                                {t(ASYNC_REQUEST_T_KEYS[buildTranslationKey(type)])}
                                {required && <RequiredMark />}
                            </div>
                            <div className='tw-flex tw-items-center'>
                                <StatusIcon
                                    success={isSuccess}
                                    inProgress={inProgress}
                                    error={isFailed}
                                    className={cx({
                                        'tw-mr-1': canRequest || canReset || resetTimeLeft,
                                        'tw-cursor-pointer': clickable
                                    })}
                                    {...(clickable && {
                                        onClick: () => showParticipantAsyncRequestDetails(participant.id, type)
                                    })}
                                />
                                {canRequest &&
                                    <Icon
                                        name='plus-circle'
                                        size='sm'
                                        button
                                        onClick={() => createParticipantAsyncRequest(participant.id, type)}
                                    />}
                                {canReset &&
                                    <Icon
                                        name='x-circle'
                                        size='sm'
                                        color='alert'
                                        button
                                        onClick={() => resetParticipantAsyncRequest(participant.id, type)}
                                    />}
                            </div>
                            {resetTimeLeft &&
                                <div className='tw-flex tw-justify-end'>
                                    <div className='tw-text-right'>
                                        <Timer
                                            config={{minutes: true, seconds: true}}
                                            startDateNow
                                            endDate={resetAfterDate}
                                            onDiffChange={() => onTimerChange(type)}
                                        />
                                    </div>
                                </div>}
                        </div>
                    </Fragment>
                );
            })}
        </div>);
};
